import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
    /** Callback function to fetch more data */
    onLoadMore: () => void;
    /** Whether data is currently being loaded */
    isLoading: boolean;
    /** Whether there is more data to load */
    hasMore: boolean;
    /** Distance from bottom (in pixels) to trigger load. Default: 300 */
    threshold?: number;
}

/**
 * Hook for efficient infinite scroll using Intersection Observer
 * Triggers onLoadMore when user scrolls near the bottom
 */
export const useInfiniteScroll = ({
                                      onLoadMore,
                                      isLoading,
                                      hasMore,
                                      threshold = 300,
                                  }: UseInfiniteScrollOptions) => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;
            
            // Only trigger if sentinel is visible, not loading, and has more data
            if (entry.isIntersecting && !isLoading && hasMore) {
                onLoadMore();
            }
        },
        [isLoading, hasMore, onLoadMore]
    );

    useEffect(() => {
        // Disconnect existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer
        const observer = new IntersectionObserver(handleIntersection, {
            root: null, // viewport
            rootMargin: `${threshold}px`, // trigger when within threshold px of viewport
            threshold: 0,
        });

        observerRef.current = observer;

        // Observe sentinel element
        const sentinel = sentinelRef.current;
        if (sentinel) {
            observer.observe(sentinel);
        } else {
            console.warn('Sentinel element not found');
        }

        // Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleIntersection, threshold]);

    return sentinelRef;
};

